import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Compass,
  HeartHandshake,
  Clock,
  User,
  Menu,
  X,
  Instagram,
  Mail,
  Moon,
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import api from "../api";

const MOBILE_NAV = [
  { label: "Accueil", route: "/", icon: Sparkles, testid: "mobile-nav-home" },
  { label: "Tirages", route: "/guidances", icon: Compass, testid: "mobile-nav-guidances" },  { label: "Énergétique", route: "/energetique", icon: HeartHandshake, testid: "mobile-nav-energetique" },
  { label: "Mes séances", route: "/espace", icon: Clock, testid: "mobile-nav-sessions" },
  { label: "Mon espace", route: "/espace", icon: User, testid: "mobile-nav-account" },
];

const DESKTOP_LINKS = [
  { label: "Accueil", route: "/" },
  { label: "Tirages", route: "/guidances" },
  { label: "Énergétique", route: "/energetique" },
  { label: "À propos", route: "/a-propos" },
  { label: "FAQ", route: "/faq" },
  { label: "Contact", route: "/contact" },
];

const FOOTER_LEGAL = [
  { label: "À propos", route: "/a-propos" },
  { label: "Tarifs", route: "/tarifs" },
  { label: "Cartomancie", route: "/cartomancie" },
  { label: "Tirage à distance", route: "/tirage-de-cartes-a-distance" },
  { label: "Contact", route: "/contact" },
  { label: "FAQ", route: "/faq" },
  { label: "CGV", route: "/cgv" },
  { label: "Mentions légales", route: "/mentions-legales" },
  { label: "Politique de confidentialité", route: "/confidentialite" },
  { label: "Politique des cookies", route: "/cookies" },
  { label: "Avertissement", route: "/avertissement" },
  { label: "Rétractation", route: "/retractation" },
  { label: "Réclamations", route: "/reclamations" },
  { label: "Médiation", route: "/mediation" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-grain">
      {/* Desktop header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FCFAF7]/90 backdrop-blur-md border-b border-[#7A5D58]/10">
        <div className="container-x flex items-center justify-between h-16 lg:h-20">
          <BrandLogo size={44} />
          <nav className="hidden lg:flex items-center gap-8">
            {DESKTOP_LINKS.map((l) => (
              <NavLink
                key={l.route}
                to={l.route}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive ? "text-[#5A3A4B] font-semibold" : "text-[#7A5D58] hover:text-[#5A3A4B]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/espace" data-testid="header-account-btn" className="btn-secondary !px-5 !py-2.5">
              Mon espace
            </Link>
            <Link to="/guidances" data-testid="header-cta-btn" className="btn-primary !px-5 !py-2.5">
              Choisir ma séance
            </Link>
          </div>
          <button
            className="lg:hidden text-[#5A3A4B] p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-[#FCFAF7] border-t border-[#7A5D58]/10 px-5 py-4 space-y-1">
            {DESKTOP_LINKS.map((l) => (
              <Link
                key={l.route}
                to={l.route}
                className="block py-2.5 text-[#5A3A4B] font-medium"
                data-testid={`mobilemenu-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1 pt-16 lg:pt-20">{children}</main>

      {/* Footer */}
      <footer
        className="relative text-[#F6EFE7]"
        style={{ background: "linear-gradient(to bottom, #1B082D, #12051F)" }}
        data-testid="site-footer"
      >
        {/* Discreet celestial separator */}
        <div className="container-x">
          <div className="flex items-center justify-center gap-4 pt-12">
            <span className="h-px flex-1 max-w-[200px]" style={{ background: "linear-gradient(to right, transparent, rgba(212,175,55,0.55))" }} />
            <Moon size={16} className="text-[#DFC07B] shrink-0" />
            <span className="h-px flex-1 max-w-[200px]" style={{ background: "linear-gradient(to left, transparent, rgba(212,175,55,0.55))" }} />
          </div>
        </div>
        <div className="container-x pt-10 pb-28 lg:pb-14">
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/brand-logo.png" alt="Logo" className="w-12 h-12 rounded-full bg-[#F6EFE7]" />
                <div className="font-serif text-lg leading-tight">
                  La Maison de ta
                  <br />
                  Sorcière Préférée
                </div>
              </div>
              <p className="text-sm text-[#CBB9CF] leading-relaxed max-w-xs">
                Sartore Noëmie — ta sorcière préférée.
                <br />
                Tirages intuitifs, oracles et séances énergétiques à distance. Aucun appel, aucune visio.
              </p>
            </div>
            <div>
              <div className="eyebrow !text-[#DFC07B] mb-4">Informations</div>
              <ul className="space-y-2">
                {FOOTER_LEGAL.map((l) => (
                  <li key={l.route}>
                    <Link
                      to={l.route}
                      className="text-sm text-[#E7DCEA]/85 hover:text-[#DFC07B] transition-colors"
                      data-testid={`footer-${l.route.replace(/\//g, "")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow !text-[#DFC07B] mb-4">Me contacter</div>
              {settings.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center gap-2 text-sm text-[#E7DCEA]/85 hover:text-[#DFC07B] mb-3 break-all"
                >
                  <Mail size={16} className="shrink-0" /> <span className="break-all">{settings.contact_email}</span>
                </a>
              )}
              {settings.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-[#E7DCEA]/85 hover:text-[#DFC07B]"
                >
                  <Instagram size={16} className="shrink-0" /> Instagram
                </a>
              )}
              <button
                onClick={() => navigate("/admin")}
                className="mt-6 block text-xs text-[#B5A4BA]/60 hover:text-[#DFC07B]"
                data-testid="footer-admin-link"
              >
                Espace administration
              </button>
            </div>
          </div>
          <div className="gold-divider my-8 opacity-40" />
          <p className="text-xs text-[#B5A4BA]/70 text-center">
            © {new Date().getFullYear()} SARTORE NOËMIE — La Maison de ta Sorcière Préférée. Tous droits réservés.
          </p>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#FCFAF7]/95 backdrop-blur-xl border-t border-[#7A5D58]/15 px-1 py-1.5 flex items-center justify-around"
        style={{ paddingBottom: "max(0.375rem, env(safe-area-inset-bottom))" }}
      >
        {MOBILE_NAV.map((item, i) => {
          const Icon = item.icon;
          const active = pathname === item.route || (item.route !== "/" && pathname.startsWith(item.route));
          return (
            <NavLink
              key={i}
              to={item.route}
              data-testid={item.testid}
              className="flex flex-col items-center gap-1 px-2 py-1.5 min-w-[58px]"
            >
              <Icon
                size={21}
                className={active ? "text-[#5A3A4B]" : "text-[#a58f92]"}
                strokeWidth={active ? 2.4 : 1.8}
              />
              <span className={`text-[10px] ${active ? "text-[#5A3A4B] font-semibold" : "text-[#a58f92]"}`}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
